import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Template, Block } from '@/types';
import { BlockType } from '@/types';
import BlockRenderer from '@/components/BlockRenderer';
import MultiPageBlock from '@/components/MultiPageBlock';
import { QuoteClientLogoProvider } from '@/store/quoteClientLogoContext';
import { isRenderableBlock } from '@/lib/blockHelpers';

type Props = {
  open: boolean;
  template: Template | null;
  onClose: () => void;
};

const AUTO_HEIGHT_TYPES = new Set([BlockType.INVESTMENT, BlockType.SIGNATURE_PAGE]);

function toPreviewBlocks(template: Template): Block[] {
  return (template.blocks || []).map((b: any, idx: number) => ({
    id: `tpl-preview-${template.id}-${idx}`,
    quoteId: `tpl-${template.id}`,
    type: b.type,
    order: typeof b.order === 'number' ? b.order : idx,
    content: b.content,
    styles: b.styles || {},
    createdAt: new Date(),
    updatedAt: new Date(),
  }));
}

function PreviewPage({ block, pageKey, scale, pageW, pageH }: {
  block: Block;
  pageKey: string;
  scale: number;
  pageW: number;
  pageH: number;
}) {
  const autoHeight = AUTO_HEIGHT_TYPES.has(block.type as BlockType);

  if (autoHeight) {
    return (
      <div
        key={pageKey}
        className="shadow-md shrink-0"
        style={{
          width: `${pageW * scale}mm`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pageW}mm`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <BlockRenderer block={block} isEditable={false} />
        </div>
      </div>
    );
  }

  return (
    <div
      key={pageKey}
      className="shadow-md shrink-0"
      style={{
        width: `${pageW * scale}mm`,
        height: `${pageH * scale}mm`,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pageW}mm`,
          height: `${pageH}mm`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <BlockRenderer block={block} isEditable={false} />
      </div>
    </div>
  );
}

export default function TemplatePreviewDialog({ open, template, onClose }: Props) {
  const blocks = template ? toPreviewBlocks(template).filter(isRenderableBlock) : [];
  const page = (template as any)?.layoutConfig?.page || {};
  const orientation = (page.orientation || 'landscape').toString().toLowerCase();
  const isLandscape = orientation === 'landscape';

  const pageW = isLandscape ? 297 : 210;
  const pageH = isLandscape ? 210 : 297;
  const scale = 0.45;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] w-[900px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Preview: {template?.name || ''}</DialogTitle>
        </DialogHeader>
        {!template ? null : (
          <QuoteClientLogoProvider blocks={blocks} layoutConfig={template.layoutConfig}>
          <div className="flex-1 overflow-y-auto py-2">
            {blocks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center">Geen blocks in deze template.</p>
            ) : (
              <div className="flex flex-col items-center gap-4">
                {blocks
                  .sort((a, b) => a.order - b.order)
                  .map((block) => (
                    <MultiPageBlock
                      key={block.id}
                      block={block}
                      renderPage={(pageBlock, pageKey) => (
                        <PreviewPage
                          block={pageBlock}
                          pageKey={pageKey}
                          scale={scale}
                          pageW={pageW}
                          pageH={pageH}
                        />
                      )}
                    />
                  ))}
              </div>
            )}
          </div>
          </QuoteClientLogoProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
